// src/pages/fiscalization/FiscalSummaryReportPage.tsx
// Aggregated fiscalization summary report — receipt counts, totals, and
// failure statistics for the period the backend reports on.
//
// Covers: GetFiscalSummary.
//
// Note: the generated GetFiscalSummary client accepts no query parameters
// (see @core/api/endpoints/fiscalization/get_fiscal_summary.ts — the params
// type is `{}`, dropping period_start/period_end/fiscal_point from the
// proto request). This page shows whatever period the backend returns
// rather than offering a date-range control that could not actually reach
// the server — a corebuilder generator gap, not a UI omission.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useFiscalizationReport, useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import type { FiscalSummary } from '@core/types/fiscalization';
import { formatDate, formatMoney } from '@/features/fiscalization/format';

export const FiscalSummaryReportPage = observer(() => {
  const store = useFiscalizationReport();
  const { t } = useI18n();
  const [summary, setSummary] = useState<FiscalSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    store.GetFiscalSummary()
      .then((res) => setSummary(res.summary ?? null))
      .catch(() => setError(t('fiscalization.reports.loadError')));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/fiscalization" className="text-muted-foreground hover:text-foreground">{t('fiscalization.dashboard.title')}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('fiscalization.reports.title')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('fiscalization.reports.title')}</h1>
        {summary && (
          <p className="text-sm text-muted-foreground mt-1">
            {formatDate(summary.period_start)} – {formatDate(summary.period_end)}
          </p>
        )}
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {store.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !summary ? (
        <p className="text-sm text-muted-foreground">{t('fiscalization.reports.empty')}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.reports.totalReceipts')}</p><p className="text-2xl font-bold text-foreground">{summary.total_receipts}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.reports.fiscalized')}</p><p className="text-2xl font-bold text-foreground">{summary.fiscalized_count}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.reports.failed')}</p><p className="text-2xl font-bold text-destructive">{summary.failed_count}</p></CardContent></Card>
            <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">{t('fiscalization.reports.abandoned')}</p><p className="text-2xl font-bold text-foreground">{summary.abandoned_count}</p></CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle>{t('fiscalization.reports.totals')}</CardTitle></CardHeader>
            <CardContent>
              <dl className="divide-y divide-border text-sm">
                <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.reports.totalAmount')}</dt><dd className="col-span-2">{formatMoney(summary.total_amount)}</dd></div>
                <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.reports.totalRefundAmount')}</dt><dd className="col-span-2">{formatMoney(summary.total_refund_amount)}</dd></div>
                <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('fiscalization.reports.country')}</dt><dd className="col-span-2">{summary.country_code || '--'}</dd></div>
              </dl>
            </CardContent>
          </Card>

          {summary.vat_totals?.totals && summary.vat_totals.totals.length > 0 && (
            <Card>
              <CardHeader><CardTitle>{t('fiscalization.reports.vatBreakdown')}</CardTitle></CardHeader>
              <CardContent>
                <ul className="divide-y divide-border text-sm">
                  {summary.vat_totals.totals.map((vt, i) => (
                    <li key={i} className="py-2 flex justify-between">
                      <span className="text-muted-foreground">{vt.vat_type}</span>
                      <span>{formatMoney(vt.vat_amount)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
});

export default FiscalSummaryReportPage;
